import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Wrench, ArrowCounterClockwise } from "@phosphor-icons/react";

export const ProjectToolsPanel = ({ projectId }) => {
  const { hasRole } = useAuth();
  const canEdit = hasRole("pm", "inginer", "admin");
  const [allocs, setAllocs] = useState([]);
  const [tools, setTools] = useState([]);
  const [general, setGeneral] = useState([]);
  const [sel, setSel] = useState("");
  const [holder, setHolder] = useState("");
  const [qty, setQty] = useState(1);

  const load = useCallback(() => {
    api.get("/inventory/allocations", { params: { project_id: projectId } }).then((r) => setAllocs(r.data || [])).catch(() => {});
  }, [projectId]);
  const loadSources = useCallback(() => {
    api.get("/inventory", { params: { scope: "tool" } }).then((r) => setTools(r.data || [])).catch(() => {});
    api.get("/inventory", { params: { scope: "general" } }).then((r) => setGeneral(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); loadSources(); }, [load, loadSources]);

  const available = [
    ...tools.filter((t) => t.status === "disponibil").map((t) => ({ ...t, _kind: "Sculă" })),
    ...general.filter((g) => (g.quantity || 0) > 0).map((g) => ({ ...g, _kind: "Gestiune" })),
  ];

  const allocate = async () => {
    if (!sel) return toast.error("Alege o sculă sau un material");
    try {
      await api.post("/inventory/allocate", { source_item_id: sel, project_id: projectId, quantity: Number(qty) || 1, holder });
      toast.success("Alocat pe proiect");
      setSel(""); setHolder(""); setQty(1);
      load(); loadSources();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const ret = async (aid) => {
    if (!window.confirm("Returnezi acest articol în gestiune?")) return;
    try { await api.post(`/inventory/allocations/${aid}/return`); toast.success("Returnat în gestiune"); load(); loadSources(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const active = allocs.filter((a) => a.status !== "returnat");

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="project-tools-panel">
      <div className="mb-4 flex items-center gap-2">
        <Wrench size={20} weight="bold" className="text-primary" />
        <h3 className="font-head text-lg font-bold">Scule & Utilaje alocate</h3>
      </div>

      {canEdit && (
        <div className="mb-5 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-white/[0.02] p-3">
          <div className="grid min-w-[260px] flex-1 gap-1.5">
            <Label>Sculă / material disponibil</Label>
            <Select value={sel} onValueChange={setSel}>
              <SelectTrigger data-testid="project-alloc-source"><SelectValue placeholder="Alege din gestiune…" /></SelectTrigger>
              <SelectContent>
                {available.map((a) => <SelectItem key={a.id} value={a.id}>[{a._kind}] {a.name}{a.code ? ` · ${a.code}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid w-24 gap-1.5"><Label>Cant.</Label><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="project-alloc-qty" /></div>
          <div className="grid min-w-[180px] gap-1.5"><Label>Responsabil</Label><Input value={holder} onChange={(e) => setHolder(e.target.value)} placeholder="șef șantier" data-testid="project-alloc-holder" /></div>
          <Button onClick={allocate} data-testid="project-alloc-submit">Alocă pe proiect</Button>
        </div>
      )}

      {active.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="project-tools-empty">Nicio sculă/material alocat pe acest proiect.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
              <th className="px-3 py-2">Denumire</th><th className="px-3 py-2">Sursă</th><th className="px-3 py-2">Cant.</th>
              <th className="px-3 py-2">Responsabil</th><th className="px-3 py-2">Alocat</th><th className="px-3 py-2"></th>
            </tr></thead>
            <tbody>
              {active.map((a) => (
                <tr key={a.id} className="border-b border-border/60" data-testid={`project-alloc-${a.id}`}>
                  <td className="px-3 py-2.5 font-medium">{a.source_name}{a.source_code ? <span className="text-xs text-muted-foreground"> · {a.source_code}</span> : ""}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{a.source_scope === "tool" ? "Scule" : "Generală"}</td>
                  <td className="px-3 py-2.5">{a.quantity} {a.unit}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{a.holder || "—"}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{fmtDate(a.allocated_at)} · {a.allocated_by_name || ""}</td>
                  <td className="px-3 py-2.5">
                    {canEdit && <button onClick={() => ret(a.id)} className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`project-alloc-return-${a.id}`}><ArrowCounterClockwise size={14} /> Returnează</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
